import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Auth } from '@shared/auth';
import { Observable } from 'rxjs/Observable';
import {
  catchError,
  filter,
  map,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import { pipe } from 'rxjs/util/pipe';

import { DATASTORE_CONFIG } from './datastore.config';
import { DatastoreConfig } from './datastore.interface';

import {
  backend,
  Backend,
  BackendConfig,
  BackendFetchTypes,
  BackendPushPayloadTypes,
  BackendPushReturnTypes,
  backendSearch,
  Params,
} from './backend';
import { isDefined, Query, ReferenceList, RootModelEntity } from './helpers';
import { RootModel, RootModelSearch } from './root.model';

export type ApiResponseStatus = 'success' | 'error';

export type ApiFetchResponse<T extends keyof (RootModel & RootModelSearch)> =
  | { status: 'success'; result: BackendFetchTypes[T] }
  | { status: 'error'; message: string };

export type ApiPostResponse<T extends keyof RootModel> =
  | { status: 'success'; result: BackendPushReturnTypes[T] }
  | { status: 'error'; message: string };

@Injectable()
export class ApiHttp {
  constructor(
    private http: HttpClient,
    private auth: Auth,
    @Inject(DATASTORE_CONFIG) private datastoreConfig: DatastoreConfig,
  ) {}

  fetch<T extends keyof RootModel>(
    ref: ReferenceList<T>,
  ): Observable<ApiFetchResponse<T>> {
    return Observable.of(ref).pipe(
      switchMap(({ pathList, query }) =>
        Observable.of((<Backend<T>>backend[pathList[0]]).fetch).pipe(
          filter(isDefined),
          map(fetch => fetch(pathList[1], query)),
          this.fetchHelper(query),
        ),
      ),
    );
  }

  fetchSearch<T extends keyof RootModelSearch>(
    key: T,
    query: Observable<string>,
  ): Observable<BackendFetchTypes[T]> {
    return query.pipe(
      map(q => backendSearch[key].fetch(q)),
      this.fetchHelper<T>(),
      map(response => (response.status === 'success' ? response.result : [])), // FIXME: Error handling?
    );
  }

  private fetchHelper<T extends keyof (RootModel)>(
    query?: Query<RootModelEntity<T>>,
  ): ((source: Observable<BackendConfig>) => Observable<ApiFetchResponse<T>>);

  private fetchHelper<T extends keyof (RootModel & RootModelSearch)>(): ((
    source: Observable<BackendConfig>,
  ) => Observable<ApiFetchResponse<T>>);

  private fetchHelper<T extends keyof (RootModel & RootModelSearch)>(
    query?: Query<RootModelEntity<any>>,
  ): ((source: Observable<BackendConfig>) => Observable<ApiFetchResponse<T>>) {
    return pipe(
      withLatestFrom(this.auth.getAuthorizationHeader()),
      switchMap(([config, authHeader]: [BackendConfig, HttpHeaders]) => {
        const baseUrl = config.isGaf
          ? this.datastoreConfig.ajaxBaseUrl
          : this.datastoreConfig.apiBaseUrl;

        const pagination = {
          limit: query && query.limit,
          offset: query && query.offset,
        };

        // Add webapp=1 for tracking in the backend to API requests
        const configParams = config.isGaf
          ? config.params
          : { ...config.params, webapp: 1 };
        const params = pagination
          ? {
              ...configParams,
              limit: pagination.limit,
              offset: pagination.offset,
            }
          : configParams;

        return this.http.get<ApiFetchResponse<T>>(
          `${baseUrl}/${config.endpoint}`,
          {
            headers: authHeader,
            params: this.serialize(params),
          },
        );
      }),
      catchError((err: HttpErrorResponse) => {
        if (err.error instanceof Error) {
          // A client-side or network error occurred.
          return Observable.throw(err.error);
        }
        // The backend returned an unsuccessful response code.
        return Observable.throw(new Error(err.message));
      }),
    );
  }

  post<T extends keyof RootModel>(
    config: BackendConfig,
    payload: BackendPushPayloadTypes[T],
  ): Observable<ApiPostResponse<T>> {
    return this.mutate(config, { action: 'post', payload });
  }

  put<T extends keyof RootModel>(
    config: BackendConfig,
    payload: BackendPushPayloadTypes[T],
  ): Observable<ApiPostResponse<T>> {
    return this.mutate(config, { action: 'put', payload });
  }

  delete<T extends keyof RootModel>(
    config: BackendConfig,
  ): Observable<ApiPostResponse<T>> {
    return this.mutate(config, { action: 'delete' });
  }

  private mutate<T extends keyof RootModel>(
    config: BackendConfig,
    data:
      | { action: 'post'; payload: BackendPushPayloadTypes[T] }
      | { action: 'put'; payload: BackendPushPayloadTypes[T] }
      | { action: 'delete' },
  ): Observable<ApiPostResponse<T>> {
    return Observable.of(config).pipe(
      withLatestFrom(this.auth.getAuthorizationHeader()),
      switchMap(([newConfig, authHeader]) => {
        const baseUrl = newConfig.isGaf
          ? this.datastoreConfig.ajaxBaseUrl
          : this.datastoreConfig.apiBaseUrl;

        const url = `${baseUrl}/${newConfig.endpoint}`;
        const options = { headers: authHeader };

        if (data.action === 'delete') {
          return this.http.delete<ApiPostResponse<T>>(url, options);
        }

        const body = this.serialize({
          ...(<Params>data.payload), // TODO: https://github.com/Microsoft/TypeScript/issues/15300
          ...newConfig.params,
        });

        if (data.action === 'post') {
          return this.http.post<ApiPostResponse<T>>(url, body, options);
        }
        return this.http.put<ApiPostResponse<T>>(url, body, options);
      }),
    );
  }

  private serialize(obj: Partial<Params>): HttpParams {
    let params = new HttpParams();
    Object.entries(obj).forEach(([key, value]) => {
      if (typeof value === 'string') {
        params = params.set(key, value);
      } else if (typeof value === 'number') {
        params = params.set(key, String(value));
      } else if (Array.isArray(value)) {
        // This type casting shouldn't be necessary but is because of a TS bug.
        // https://github.com/Microsoft/TypeScript/issues/15315
        // https://github.com/Microsoft/TypeScript/issues/7294

        // Our backend expects arrays foo=[1,2] to be encoded as foo[]=1&foo[]=2
        (value as (string | number)[]).forEach(rawVal =>
          // The batching of requests connatenates things with `&`.
          String(rawVal)
            .split('&')
            .forEach(v => (params = params.append(`${key}[]`, v))),
        );
      }
    });
    params = params.set('compact', 'true');
    return params;
  }

  /*
  private handleException(error: Response | any) {
    // In a real world app, we might use a remote logging infrastructure
    let errMsg: string;
    if (error instanceof Response) {
      const body = error.json() || '';
      const err = body.error || JSON.stringify(body);
      errMsg = `${error.status} - ${error.statusText || ''} ${err}`;
    } else {
      errMsg = error.message ? error.message : error.toString();
    }
    console.error(errMsg);
    return Observable.throw(errMsg);
  }
  */
}



// WEBPACK FOOTER //
// /mnt/jenkins-slave-workspace/workspace/gaf-build-webapp-compat/webapp/src/app/@shared/datastore/api-http.service.ts